import React, { useState, useEffect, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import CountUp from "react-countup";
import VisibilitySensor from "react-visibility-sensor";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCreative } from "swiper/modules";
import "swiper/css";
import "./Home.css"
import bannerImage from "../assets/ventures.png";

// CSS Styles for the futuristic design
const futuristicStyles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.futuristic-wrapper {
  font-family: 'Inter', sans-serif;
}

/* Base styles */
.futuristic-bg {
  background: #0a0e17;
  position: relative;
  overflow: hidden;
}

/* Floating orbs */
.futuristic-orbs {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
}

.futuristic-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.3;
  animation: float-orb 15s ease-in-out infinite;
}

.orb-1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #1a5cff, #00b8ff);
  top: 10%;
  left: 5%;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #6651ff, #1a5cff);
  top: 60%;
  right: 10%;
  animation-delay: -5s;
}

.orb-3 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #00b8ff, #6651ff);
  bottom: 10%;
  left: 20%;
  animation-delay: -10s;
}

@keyframes float-orb {
  0%, 100% {
    transform: translateY(0px) scale(1);
  }
  33% {
    transform: translateY(-30px) scale(1.05);
  }
  66% {
    transform: translateY(30px) scale(0.95);
  }
}

/* Grid overlay */
.futuristic-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(26, 92, 255, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(26, 92, 255, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
  z-index: 1;
  opacity: 0.3;
}

/* Scan lines */
.futuristic-scanlines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    transparent 50%,
    rgba(0, 184, 255, 0.03) 50%
  );
  background-size: 100% 4px;
  pointer-events: none;
  z-index: 2;
  animation: scanline 8s linear infinite;
}

@keyframes scanline {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

/* Glassmorphism cards */
.futuristic-glass {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.futuristic-glass::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #00b8ff, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.futuristic-glass:hover {
  border-color: rgba(0, 184, 255, 0.3);
  box-shadow: 
    0 8px 32px rgba(0, 184, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.futuristic-glass:hover::before {
  opacity: 1;
}

/* Gradient text */
.futuristic-gradient-text {
  background: linear-gradient(135deg, #00b8ff, #6651ff, #1a5cff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Animated counters */
.futuristic-counter {
  position: relative;
  display: inline-block;
}

.futuristic-counter::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #00b8ff, #6651ff);
  transition: width 0.3s ease;
}

.futuristic-counter:hover::after {
  width: 100%;
}

/* Hover scale animations */
.futuristic-scale-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.futuristic-scale-hover:hover {
  transform: scale(1.05);
}

/* Neon glow effects */
.futuristic-glow {
  filter: drop-shadow(0 0 10px rgba(0, 184, 255, 0.3));
}

.futuristic-glow:hover {
  filter: drop-shadow(0 0 20px rgba(0, 184, 255, 0.5));
}

/* Floating particles */
.futuristic-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.futuristic-particle {
  position: absolute;
  width: 2px;
  height: 2px;
  background: #00b8ff;
  border-radius: 50%;
  animation: float-particle 6s ease-in-out infinite;
}

@keyframes float-particle {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  50% {
    transform: translateY(-100px) translateX(20px);
  }
}

/* Modal styles */
.futuristic-modal {
  background: rgba(10, 14, 23, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 184, 255, 0.2);
  border-radius: 16px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.futuristic-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #00b8ff, #6651ff, transparent);
}

/* Swiper Navigation Arrows */
.futuristic-swiper {
  position: relative;
}

.futuristic-swiper .swiper-button-disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: scale(1) !important;
}

/* Mobile responsiveness for arrows */
@media (max-width: 640px) {
  .futuristic-carousel-arrow {
    padding: 8px !important;
  }
  
  .futuristic-carousel-arrow svg {
    width: 16px !important;
    height: 16px !important;
  }
}

/* Hover effects for arrows */
.futuristic-carousel-arrow:hover:not(.swiper-button-disabled) {
  background: rgba(0, 184, 255, 0.3) !important;
  box-shadow: 0 0 20px rgba(0, 184, 255, 0.5);
}

/* Carousel container padding for arrows */
.futuristic-carousel-container {
  position: relative;
  padding: 0 40px;
}

@media (max-width: 768px) {
  .futuristic-carousel-container {
    padding: 0 30px;
  }
}
`;

// Services data
const services = [
  { title: "Full Stack Web Development", description: "Using latest tech stack like Spring Boot (Java), Node.js, ReactJS etc.", icon: "🧱", gradient: "from-purple-500 to-pink-500" },
  { title: "Mobile Apps", description: "Android & iOS apps with great UX.", icon: "📱", gradient: "from-blue-500 to-cyan-500" },
  { title: "AI Solutions", description: "Automated systems powered by machine learning.", icon: "🤖", gradient: "from-green-500 to-emerald-500" },
  { title: "Cloud Migration", description: "Seamless migration to AWS, GCP & Azure.", icon: "☁️", gradient: "from-orange-500 to-red-500" },
  { title: "Fintech Applications", description: "Secure, scalable fintech apps & tools.", icon: "💰", gradient: "from-yellow-500 to-amber-500" },
  { title: "E-Commerce Solutions", description: "Custom online stores with payment integration & inventory systems.", icon: "🛒", gradient: "from-indigo-500 to-purple-500" },
  { title: "IoT Dashboards", description: "Live device monitoring with modern dashboards.", icon: "📡", gradient: "from-teal-500 to-blue-500" },
  { title: "Data Analytics", description: "Real-time insights using big data and visualization tools.", icon: "📊", gradient: "from-rose-500 to-pink-500" },
  { title: "Financial Research", description: "Financial Analysis, Credit Research, Investment Banking.", icon: "💹", gradient: "from-lime-500 to-green-500" }
];

// Hero text
const textLine1 = "Innovating the Future of";
const textLine2 = "Digital Solutions";

// Letter animation for hero
const letterAnimation = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08 },
  }),
};

// Reusable Floating Orbs Component
const FloatingOrbs = () => {
  return (
    <div className="futuristic-orbs">
      <div className="futuristic-orb orb-1"></div>
      <div className="futuristic-orb orb-2"></div>
      <div className="futuristic-orb orb-3"></div>
    </div>
  );
};

// Reusable Grid Overlay Component
const GridOverlay = () => {
  return (
    <>
      <div className="futuristic-grid"></div>
      <div className="futuristic-scanlines"></div>
    </>
  );
};

// Reusable Floating Particles Component
const FloatingParticles = () => {
  return (
    <div className="futuristic-particles">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="futuristic-particle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 6}s`,
            animationDuration: `${4 + Math.random() * 4}s`,
          }}
        ></div>
      ))}
    </div>
  );
};

// Reusable Glass Card Component
const GlassCard = ({ children, className = "", hoverable = true }) => {
  return (
    <div className={`futuristic-glass ${hoverable ? 'futuristic-scale-hover' : ''} ${className}`}>
      {children}
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ end, label, suffix = "" }) => {
  const [hasStarted, setHasStarted] = useState(false);

  return (
    <div className="text-center group">
      <div className="relative inline-block">
        <h4 className="fw-bold text-5xl lg:text-6xl mb-2 relative">
          <CountUp end={end} duration={2.5} start={hasStarted ? undefined : 0}>
            {({ countUpRef, start }) => (
              <VisibilitySensor
                onChange={(isVisible) => {
                  if (isVisible) {
                    setHasStarted(true);
                    start();
                  }
                }}
                delayedCall
              >
                <span
                  ref={countUpRef}
                  className="futuristic-counter futuristic-gradient-text font-black"
                />
              </VisibilitySensor>
            )}
          </CountUp>
          {suffix && <span className="text-2xl ml-1 futuristic-gradient-text">{suffix}</span>}
        </h4>
      </div>
      <p className="text-gray-300 font-semibold text-lg mt-3 uppercase tracking-wider">{label}</p>
    </div>
  );
};

// Service Card
const ServiceCard = ({ title, description, icon, gradient, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <GlassCard
      hoverable={true}
      className="p-6 h-full overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-10 transition-all duration-500 ${isHovered ? 'scale-110' : 'scale-100'}`}></div>

      <div className="absolute inset-0 opacity-30">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10">
        <div className="text-6xl mb-6 transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 futuristic-glow">
          {icon}
        </div>
        <h4 className="text-2xl font-bold mb-4 leading-tight text-white">{title}</h4>
        <p className="text-gray-300 leading-relaxed">{description}</p>
      </div>
    </GlassCard>
  );
};

// Futuristic Carousel - CORRECTED VERSION
const FuturisticCarousel = ({ items }) => {
  const swiperRef = useRef(null);

  return (
    <div className="relative w-full futuristic-carousel-container">
      
      {/* Arrows below carousel */}
      <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 flex gap-8 z-10">
        {/* Left Arrow */}
        <button
          className="bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-2xl 
                     p-3 transition-all duration-300 futuristic-glass futuristic-glow 
                     hover:scale-110"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          className="bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-2xl 
                     p-3 transition-all duration-300 futuristic-glass futuristic-glow 
                     hover:scale-110"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Swiper Section */}
      <Swiper
        modules={[Autoplay, EffectCreative]}
        effect="creative"
        creativeEffect={{
          prev: { shadow: true, translate: ["-100%", 0, -400] },
          next: { shadow: true, translate: ["100%", 0, -400] },
        }}
        autoplay={{
          delay: 2000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true, // 🛑 This pauses autoplay on hover
        }}
        speed={400}
        loop={true}
        grabCursor={true}
        centeredSlides={true}
        slidesPerView={1}
        spaceBetween={20}
        className="futuristic-swiper"
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        breakpoints={{
          640: { slidesPerView: 1, spaceBetween: 20 },
          768: { slidesPerView: 2, spaceBetween: 30 },
          1024: { slidesPerView: 3, spaceBetween: 40 },
          1280: { slidesPerView: 4, spaceBetween: 50 },
        }}
      >
        {items.map((service, index) => (
          <SwiperSlide
            key={index}
            className="flex justify-center"
            onMouseEnter={() => swiperRef.current?.autoplay.stop()} // ⛔ Stop on hover
            onMouseLeave={() => swiperRef.current?.autoplay.start()} // ▶ Resume on leave
          >
            <ServiceCard {...service} index={index} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

// Reusable Modal Component
export const FuturisticModal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="futuristic-modal relative max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold futuristic-gradient-text">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors duration-200 text-2xl"
            >
              ×
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// Main Wrapper Component
export const FuturisticWrapper = ({ children, className = "" }) => {
  return (
    <>
      <style>{futuristicStyles}</style>
      <div className={`futuristic-wrapper futuristic-bg ${className}`}>
        <FloatingOrbs />
        <GridOverlay />
        <FloatingParticles />
        {children}
      </div>
    </>
  );
};

// Home Component
const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <FuturisticWrapper>
      <div className="min-h-screen text-white font-sans overflow-hidden relative z-10">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 my-6 font-sans">
          <div className="max-w-7xl mx-auto py-6 lg:px-8">
            <div className={`flex flex-col lg:flex-row items-start justify-between gap-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Text */}
              <div className="lg:w-1/2 text-left space-y-8">
                <div className="space-y-4">
                  <motion.h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-snug" initial="hidden" animate="visible">
                    {textLine1.split("").map((char, i) => (
                      <motion.span key={i} custom={i} variants={letterAnimation}>{char === " " ? "\u00A0" : char}</motion.span>
                    ))}
                  </motion.h1>
                  <motion.h1
                    className="relative text-4xl lg:text-5xl xl:text-6xl font-extrabold futuristic-gradient-text inline-block whitespace-pre-wrap"
                    initial="hidden"
                    animate="visible"
                    style={{
                      overflow: "visible",
                      lineHeight: "1.3",
                      paddingBottom: "0.3em",
                    }}
                  >
                    {textLine2.split("").map((char, i) => (
                      <motion.span
                        key={i}
                        custom={i}
                        variants={letterAnimation}
                        style={{
                          display: "inline-block",
                          overflow: "visible",
                          lineHeight: "1.2",
                        }}
                      >
                        {char === " " ? "\u00A0" : char}
                      </motion.span>
                    ))}
                  </motion.h1>
                </div>
                <div className="space-y-6 mt-6">
                  <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
                    We build <span className="font-bold text-white">innovative, future-ready software solutions</span> for industries like 
                    <span className="font-medium text-white"> e-commerce, fintech, AI, Generative AI, IoT,and many more enterprise domains</span>.
                  </p>
                  <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
                    From concept to execution, we transform your ideas into <span className="font-bold text-white">intelligent, scalable products</span> that drive success.
                  </p>
                  <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
                    Whether you're a startup or an enterprise, we engineer experiences that <span className="font-bold text-white">automate, simplify, and grow your digital journey</span>.
                  </p>
                </div>
                <div className="pt-4">
                  <p className="text-2xl lg:text-3xl font-black futuristic-gradient-text">
                    Your vision, our innovation.
                  </p>
                </div>
              </div>

              {/* Hero Image */}
              <div className="lg:w-1/2 flex justify-center lg:justify-end">
                <GlassCard className="p-4">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/30 to-purple-600/30 rounded-3xl blur-2xl -z-10 animate-pulse"></div>
                    <img 
                      src={bannerImage} 
                      alt="Naught Venture Digital Solutions" 
                      className="w-full max-w-lg h-[500px] rounded-2xl object-cover shadow-inner futuristic-glow" 
                    />
                    <div className="absolute -top-4 -right-4 w-8 h-8 bg-cyan-400 rounded-full animate-bounce futuristic-glow"></div>
                    <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-purple-400 rounded-full animate-bounce delay-500 futuristic-glow"></div>
                  </div>
                </GlassCard>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="relative px-6 lg:px-8 py-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="mb-16">
              <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
                Our <span className="futuristic-gradient-text">Digital Arsenal</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Comprehensive futuristic solutions powered by cutting-edge technologies
              </p>
            </div>
            <FuturisticCarousel items={services} />
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="relative py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
                About <span className="futuristic-gradient-text">Naught Venture</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-4xl leading-relaxed mx-auto">
                Naught Venture Advisors Pvt Ltd is a full-stack digital product engineering and managed software development company delivering impactful solutions across fintech, analytics, AI, ML, IoT, cloud, and a broad spectrum of enterprise domains.
              </p>
            </div>

            <GlassCard className="p-8 lg:p-12 max-w-6xl mx-auto">
              <Row className="text-center">
                {[
                  { label: "Projects Delivered", end: 100 },
                  { label: "Industries Served", end: 10 },
                  { label: "Client Satisfaction", end: 100, suffix: "%" },
                  { label: "Years of Experience", end: 13 }
                ].map((stat, index) => (
                  <Col md={3} sm={6} xs={6} key={index} className="mb-8">
                    <AnimatedCounter {...stat} />
                  </Col>
                ))}
              </Row>
            </GlassCard>
          </div>
        </section>

        {/* Extra CSS */}
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          .animate-float { animation: float 6s ease-in-out infinite; }
        `}</style>
      </div>
    </FuturisticWrapper>
  );
};

export default Home;